import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { adminNavGroups, gameScopeGroups } from '../lib/registry';
import { useAdminSession } from '../lib/session';
import { Modal } from './Modal';
import { Pill } from './StateCard';
import { lifecycleLabel, PLATFORM_LABEL } from './site';
import { valueLabel } from '../lib/labels';

/** Development banner. Never rendered in a production build (see vite define). */
function DevBanner({ environment }: { environment: string }) {
  if (import.meta.env.PROD && environment === 'production') return null;

  return (
    <div className="dev-banner">
      <span>
        <strong>{valueLabel(environment)}</strong> 환경 · 개발 데이터. 실제 이용자 데이터와 분리
      </span>
      <span>외부 메일 발송 없음</span>
    </div>
  );
}

export function AdminShell() {
  const session = useAdminSession();
  const location = useLocation();
  const [menuQuery, setMenuQuery] = useState('');
  const [scopeDetailOpen, setScopeDetailOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Game Scope swaps the navigation to the per-game view of the same screens.
  const groups = useMemo(() => {
    const base = (session.scopeGameId ? gameScopeGroups() : adminNavGroups())
      .map((group) => ({
        ...group,
        pages: group.pages.filter((page) => {
          const parameters = page.route.match(/:\w+/g) ?? [];
          return parameters.length === 0
            || (Boolean(session.scopeGameId) && parameters.every((parameter) => parameter === ':gameId'));
        }),
      }))
      .filter((group) => group.pages.length > 0);
    if (!menuQuery.trim()) return base;

    const needle = menuQuery.trim().toLowerCase();
    return base
      .map((group) => ({
        name: group.name,
        pages: group.pages.filter(
          (page) =>
            page.title.toLowerCase().includes(needle) ||
            page.id.toLowerCase().includes(needle) ||
            page.route.toLowerCase().includes(needle),
        ),
      }))
      .filter((group) => group.pages.length > 0);
  }, [session.scopeGameId, menuQuery]);

  if (session.loading) {
    return <div className="page"><div className="state-card"><div className="state-card__title">불러오는 중</div></div></div>;
  }

  if (!session.identity) {
    return (
      <div className="page">
        <div className="state-card">
          <div className="state-card__title">관리자 로그인 필요</div>
          <div className="state-card__actions">
            <Link className="btn btn--primary" to="/admin/sign-in">로그인으로 이동</Link>
          </div>
        </div>
      </div>
    );
  }

  const { identity } = session;

  return (
    <>
      <DevBanner environment={identity.environment} />
      <div className="admin-shell">
        <nav className={`admin-nav${navOpen ? ' admin-nav--open' : ''}`} aria-label="운영 콘솔 내비게이션">
          <div className="admin-nav__brand">
            <img src="/ak-mark.png" alt="" />
            <span>ASKRS Games</span>
            <button
              type="button"
              className="btn"
              style={{ marginLeft: 'auto', padding: '2px 8px' }}
              onClick={() => setNavOpen((open) => !open)}
              aria-expanded={navOpen}
            >
              메뉴
            </button>
          </div>
          <div className="admin-nav__search">
            <input
              type="search"
              placeholder="메뉴 검색"
              value={menuQuery}
              onChange={(event) => setMenuQuery(event.target.value)}
              aria-label="메뉴 검색"
            />
          </div>
          {session.scopeGameId && (
            <div className="admin-nav__group">
              <button
                type="button"
                className="btn"
                style={{ margin: '0 16px', width: 'calc(100% - 32px)' }}
                onClick={() => session.setScopeGameId(null)}
              >
                ← 전체 범위로
              </button>
            </div>
          )}
          {groups.map((group) => (
            <div className="admin-nav__group" key={group.name}>
              <div className="admin-nav__group-title">{group.name}</div>
              {group.pages.map((page) => (
                <NavLink
                  key={page.id}
                  to={session.scopeGameId ? page.route.replace(':gameId', encodeURIComponent(session.scopeGameId)) : page.route}
                  onClick={() => setNavOpen(false)}
                  className="admin-nav__item"
                  aria-current={location.pathname === page.route ? 'page' : undefined}
                >
                  <span>{page.title}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-main">
          <header className="context-bar">
            <label className="visually-hidden" htmlFor="scope-picker">범위 선택</label>
            <select
              id="scope-picker"
              value={session.scopeGameId ?? ''}
              onChange={(event) => session.setScopeGameId(event.target.value || null)}
            >
              <option value="">전체 게임</option>
              {session.games.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.name} · {game.platforms.map((platform) => PLATFORM_LABEL[platform] ?? platform).join('/')} · {lifecycleLabel(game.lifecycle)}
                </option>
              ))}
            </select>

            <Pill tone="info">{valueLabel(identity.environment)}</Pill>

            <div className="context-bar__spacer" />

            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              시간 {identity.display_timezone}
            </span>
            {!identity.mfa_satisfied && <Pill tone="warn">MFA 미완료</Pill>}
            <span style={{ fontSize: 13 }}>{identity.admin.name}</span>
            <button type="button" className="btn" onClick={() => void session.signOut()}>로그아웃</button>
          </header>

          {session.scopeGame && (
            <div className="scope-bar">
              <strong>{session.scopeGame.name}</strong>
              <span className="scope-bar__summary">
                · {session.scopeGame.platforms.map((platform) => PLATFORM_LABEL[platform] ?? platform).join(' / ')}
                <Pill tone={session.scopeGame.lifecycle === 'live' ? 'ok' : 'neutral'}>
                  {lifecycleLabel(session.scopeGame.lifecycle)}
                </Pill>
              </span>
              <span className="scope-bar__hint" style={{ color: 'var(--text-secondary)' }}>
                선택한 게임 데이터만 조회
              </span>
              <button type="button" className="btn" onClick={() => setScopeDetailOpen(true)}>범위 상세</button>
              <button type="button" className="btn" onClick={() => session.setScopeGameId(null)}>전체 범위로</button>
            </div>
          )}

          <main className="page">
            <Outlet />
          </main>
        </div>
      </div>

      {scopeDetailOpen && session.scopeGame && (
        <Modal title="범위 상세" onClose={() => setScopeDetailOpen(false)}>
          <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 8 }}>
            <dt>게임</dt><dd>{session.scopeGame.name}</dd>
            <dt>게임 ID</dt><dd style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{session.scopeGame.id}</dd>
            <dt>플랫폼</dt><dd>{session.scopeGame.platforms.map((platform) => PLATFORM_LABEL[platform] ?? platform).join(', ')}</dd>
            <dt>상태</dt><dd>{session.scopeGame.status}</dd>
            <dt>환경</dt><dd>{valueLabel(identity.environment)}</dd>
            <dt>내 권한</dt><dd>{identity.roles.map((role) => role.role).join(', ')}</dd>
          </dl>
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
            변경 전까지 선택 범위 유지 · 조회·내보내기·일괄 작업에 동일 적용
          </p>
        </Modal>
      )}
    </>
  );
}
